import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { initOneSignal } from "@/hooks/useNotifications";
import { useApp } from "@/contexts/AppContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Eye, Users, Plus, Pencil, Trash2, PauseCircle, PlayCircle, ExternalLink, MoreVertical, ArrowUp, RefreshCw, Clock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useState, useEffect, useRef } from "react";

export default function Dashboard() {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  usePullToRefresh(() => window.location.reload());

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Element;
      if (!target.closest("[data-menu]")) setOpenMenu(null);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  const { user, listings, updateListing, authLoading, isPromoted } = useApp();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" />;

  const myListings = listings.filter((l) => l.ownerId === user.id && l.status !== "expired");

  const [expiredListings, setExpiredListings] = useState<any[]>([]);
  useEffect(() => {
    if (!user) return;
    supabase.from("listings").select("*").eq("owner_id", user.id).eq("status", "expired")
      .order("expires_at", { ascending: false })
      .then(({ data }) => setExpiredListings(data || []));
  }, [user?.id]);

  const handleBump = async (id: string) => {
    const { data, error } = await supabase.rpc("bump_listing", { p_listing_id: id });
    if (error || !(data as any)?.ok) {
      alert((data as any)?.error || error?.message || "Não foi possível promover.");
      return;
    }
    alert("Anúncio promovido ao topo!");
    window.location.reload();
  };

  const handleRenew = async (id: string) => {
    const { data, error } = await supabase.rpc("renew_listing", { p_listing_id: id });
    if (error || !(data as any)?.ok) {
      alert((data as any)?.error || error?.message || "Não foi possível renovar.");
      return;
    }
    window.location.reload();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este anúncio?")) return;
    setDeleting(id);
    await supabase.from("listings").delete().eq("id", id);
    updateListing(id, { status: "expired" });
    setDeleting(null);
  };

  const handleTogglePause = async (id: string, currentStatus: string) => {
    setToggling(id);
    const newStatus = currentStatus === "active" ? "paused" : "active";
    await supabase.from("listings").update({ status: newStatus }).eq("id", id);
    updateListing(id, { status: newStatus as any });
    setToggling(null);
  };

  const statusLabel = (status: string) => {
    if (status === "active") return { label: "Ativo", className: "bg-primary/10 text-primary" };
    if (status === "paused") return { label: "Pausado", className: "bg-yellow-100 text-yellow-700" };
    if (status === "pending") return { label: "Pendente", className: "bg-accent/20 text-accent-foreground" };
    return { label: status, className: "bg-muted text-muted-foreground" };
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-heading font-bold">Olá, {user.name.split(" ")[0]}!</h1>
          <p className="text-muted-foreground text-sm">Gerencie seus anúncios e acompanhe seus resultados.</p>
        </div>
        <Link to="/criar-anuncio">
          <Button className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
            <Plus className="h-4 w-4 mr-2" /> Criar Anuncio
          </Button>
        </Link>
      </div>

      {expiredListings.length > 0 && (
        <button
          onClick={() => document.getElementById("secao-expirados")?.scrollIntoView({ behavior: "smooth" })}
          className="w-full mb-4 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm font-medium hover:bg-amber-100 transition-colors text-left"
        >
          <Clock className="h-4 w-4 shrink-0" />
          Você tem {expiredListings.length} anúncio{expiredListings.length !== 1 ? "s" : ""} expirado{expiredListings.length !== 1 ? "s" : ""} — renove para voltar ao ar. Ver agora →
        </button>
      )}
      <h2 className="text-xl font-heading font-bold mb-4">Meus Anuncios</h2>
      {myListings.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <p className="text-muted-foreground mb-4">Você ainda não tem anúncios.</p>
          <Link to="/criar-anuncio">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">Criar primeiro anúncio</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {myListings.map((l) => {
            const { label, className } = statusLabel(l.status);
            return (
              <div key={l.id} className="bg-card border border-border rounded-lg p-4 flex gap-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/anuncio/${l.id}`)}>
                {/* Imagem */}
                <img src={l.images[0]} alt={l.title} className="h-20 w-20 rounded-md object-cover shrink-0" />

                {/* Conteudo */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-heading font-semibold truncate">{l.title}</p>
                      {l.ticketNumber && <span className="text-[10px] font-mono text-muted-foreground">ANC-{l.ticketNumber}</span>}
                      <p className="text-xs text-muted-foreground mt-0.5">{l.category} — R$ {l.price}/{l.priceUnit}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium inline-block mt-1 ${className}`}>
                        {label}
                      </span>
                      {isPromoted(l.id) && (
                        <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 bg-amber-100 text-amber-700 mt-1">
                          <ArrowUp className="h-3 w-3" /> Em destaque
                        </span>
                      )}
                    </div>

                    {/* Menu tres pontos */}
                    <div className="relative shrink-0" data-menu onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenMenu(openMenu === l.id ? null : l.id)}
                        className="p-1.5 rounded-full hover:bg-secondary transition-colors"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                      {openMenu === l.id && (
                        <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-10 overflow-hidden">
                          {l.status === "active" && (
                            <button
                              onClick={() => { handleBump(l.id); setOpenMenu(null); }}
                              className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary flex items-center gap-2 text-primary font-medium"
                            >
                              <ArrowUp className="h-4 w-4" /> Promover ao topo
                            </button>
                          )}
                          {(l.status === "active" || l.status === "paused") && (
                            <button
                              onClick={() => { handleTogglePause(l.id, l.status); setOpenMenu(null); }}
                              className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary flex items-center gap-2"
                            >
                              {l.status === "active"
                                ? <><PauseCircle className="h-4 w-4 text-yellow-600" /> Pausar</>
                                : <><PlayCircle className="h-4 w-4 text-green-600" /> Reativar</>}
                            </button>
                          )}
                          <button
                            onClick={() => { navigate(`/editar-anuncio/${l.id}`); setOpenMenu(null); }}
                            className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary flex items-center gap-2"
                          >
                            <Pencil className="h-4 w-4 text-primary" /> Editar
                          </button>
                          <button
                            onClick={() => { handleDelete(l.id); setOpenMenu(null); }}
                            className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary flex items-center gap-2 text-destructive"
                          >
                            <Trash2 className="h-4 w-4" /> Excluir
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Botoes verticais - hidden */}
                    <div className="hidden flex-col gap-1.5 shrink-0">
                      {(l.status === "active" || l.status === "paused") && (
                        <Button
                          variant="outline"
                          size="sm"
                          className={`gap-1 text-xs h-8 ${l.status === "paused" ? "text-green-600 hover:text-green-700" : "text-yellow-600 hover:text-yellow-700"}`}
                          onClick={() => handleTogglePause(l.id, l.status)}
                          disabled={toggling === l.id}
                        >
                          {toggling === l.id ? "..." : l.status === "active"
                            ? <><PauseCircle className="h-3 w-3" /> Pausar</>
                            : <><PlayCircle className="h-3 w-3" /> Reativar</>
                          }
                        </Button>
                      )}
                      <Link to={`/editar-anuncio/${l.id}`}>
                        <Button variant="outline" size="sm" className="gap-1 text-xs h-8 w-full">
                          <Pencil className="h-3 w-3" /> Editar
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs h-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(l.id)}
                        disabled={deleting === l.id}
                      >
                        <Trash2 className="h-3 w-3" /> {deleting === l.id ? "..." : "Excluir"}
                      </Button>
                    </div>
                  </div>

                  {/* Metricas individuais */}
                  <div className="flex gap-4 mt-2 pt-2 border-t border-border">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Eye className="h-3 w-3" />
                      <span>{l.views} visualizações</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{l.contactsRevealed} contatos</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {expiredListings.length > 0 && (
        <div id="secao-expirados" className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-heading font-bold">Anúncios expirados</h2>
            <span className="text-xs text-muted-foreground">({expiredListings.length})</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Anúncios expirados ficam guardados aqui, visíveis só para você. Renove para voltar ao ar. Contas gratuitas têm os expirados removidos após 7 dias — planos pagos mantêm os anúncios guardados.
          </p>
          <div className="space-y-4">
            {expiredListings.map((l) => {
              const removalDate = new Date(new Date(l.expires_at).getTime() + 7 * 86400000);
              const daysLeft = Math.max(0, Math.ceil((removalDate.getTime() - Date.now()) / 86400000));
              return (
                <div key={l.id} className="bg-card border border-border rounded-lg p-4 flex gap-4 opacity-90">
                  <img src={(l.images && l.images[0]) || "/placeholder.svg"} alt={l.title} className="h-20 w-20 rounded-md object-cover shrink-0 grayscale" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold truncate">{l.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{l.category} — R$ {l.price}/{l.price_unit}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium inline-block mt-1 bg-destructive/10 text-destructive">
                      Expirado
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      Expirado há {7 - daysLeft >= 0 ? (7 - daysLeft) : 0} dia(s)
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0 justify-center">
                    <Button size="sm" className="gap-1 text-xs bg-primary text-primary-foreground"
                      onClick={() => handleRenew(l.id)}>
                      <RefreshCw className="h-3 w-3" /> Renovar
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1 text-xs text-destructive"
                      onClick={() => { if (confirm("Excluir permanentemente?")) { supabase.from("listings").delete().eq("id", l.id).then(() => setExpiredListings(prev => prev.filter(x => x.id !== l.id))); } }}>
                      <Trash2 className="h-3 w-3" /> Excluir
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
